import React, { useState, useEffect, useContext } from 'react'
import Icon from 'components/Icon'
import useCurrentUser from 'hooks/useCurrentUser'
import { Button } from '@arnsbogroup/react-voicearchive'
import { Link } from 'react-router-dom'
import useApi from 'hooks/useApi'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import Modal from 'components/Modal'
import ReactMarkdown from 'react-markdown'
import { BrandContext } from 'CustomerApp'
import RecentOrders from './components/RecentOrders'

const CustomerHomePage = ({notifications}) => {
  const { formatMessage } = useIntl()
  const [announcements, setAnnouncements] = useState([])
  const [modalContent, onShowModal] = useState(null)
  const { image, name, username } = useCurrentUser()
  const [hoveredRating, setHoveredRating] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [coordinators] = useApi('/rest/v2/customer/coordinators')
  const [latest] = useApi('/rest/v2/user/me/announcements')
  const [updated,,, ack] = useApi('/rest/v2/user/me/announcements', { load: false })
  const { brand } = useContext(BrandContext)
  const importantNotifications = notifications
    .filter((notification) => notification.strategy == 'orderfeedback') //filter by strategy
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()) // sort newest by created_at first
    .slice(0, 4) //show only the latest 4

  useEffect(() => setAnnouncements(updated || latest || []), [latest, updated])

  const onMarkSeen = (id, markSeenFunction) => {
    const indexToRemove = importantNotifications.findIndex(notification => notification.id == id)
    if (indexToRemove !== -1) {
      importantNotifications.splice(indexToRemove, 1)
      markSeenFunction()
    }
  }

  const ressources = [
    {
      name: formatMessage(messages.trainingTitle),
      icon: 'helicopter',
      description: formatMessage(messages.training),
      href: null,
      path: '/docs',
      bgColor: 'bg-teal-400',
    },
    {
      name: formatMessage(messages.templateTitle),
      icon: 'scroll',
      description: formatMessage(messages.template),
      href: formatMessage(messages.templateHref),
      path: null,
      bgColor: 'bg-blue-400',
    },
    {
      name: formatMessage(messages.requestTitle),
      icon: 'question',
      description: formatMessage(messages.request),
      href: formatMessage(messages.requestHref),
      path: null,
      bgColor: 'bg-yellow-400',
    },
    {
      name: formatMessage(messages.helpTitle),
      icon: 'ambulance',
      description: formatMessage(messages.help),
      href: formatMessage(messages.helpHref),
      path: null,
      bgColor: 'bg-rose-400',
    },
  ]

  return (
    <main className="flex-1 relative pb-8 z-0 overflow-y-auto">
      <Modal show={!!modalContent} onClose={() => onShowModal(null)}>
        <div className="markdown-body">
          <ReactMarkdown
            children={modalContent}
            components={{
              img: ({ node, ...props }) => <img className="border border-gray-400 shadow-sm m-8" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc" {...props } />,
            }}
          />
        </div>
      </Modal>

      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:max-w-6xl lg:mx-auto lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between lg:border-t lg:border-gray-200">
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <img className="hidden h-16 w-16 rounded-full sm:block" src={image} alt="" />
                <div>
                  <div className="flex items-center">
                    <img className="h-16 w-16 rounded-full sm:hidden" src={image} alt="" />
                    <h1 className="ml-3 text-2xl font-extrabold leading-7 sm:leading-9 sm:truncate">
                      <FormattedMessage{...messages.hello} /> { name }
                    </h1>
                  </div>
                  <dl className="mt-6 flex flex-col sm:ml-3 sm:mt-1 sm:flex-row sm:flex-wrap">
                    <dt className="sr-only"><FormattedMessage {...messages.company}/></dt>
                    <dd className="flex items-center text-sm text-gray-500 font-medium capitalize sm:mr-6">
                      <Icon icon="user" className="shrink-0 text-gray-400 mr-1.5" />
                      <span>{ username }</span>
                    </dd>
                    <dt className="sr-only"><FormattedMessage {...messages.accountStatus}/></dt>
                    <dd className="mt-3 flex items-center text-sm text-gray-500 font-medium sm:mr-6 sm:mt-0 capitalize">
                      <Icon icon="check-circle" className="shrink-0 text-green-400 mr-1.5" />
                      <span><FormattedMessage{...messages.onboarded}/></span>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
              { brand.voiceflow && 
                <Link to="/orders/new">
                  <Button
                    className="inline-flex"
                    title={formatMessage(messages.addOrder)}
                    icon={['fa', 'plus']}
                  >
                    <FormattedMessage {...messages.addOrder}/>
                  </Button>
                </Link>
              }
              <React.Fragment>
                <a target="_blank" href={formatMessage(messages.callToActionHrefSecondary)}>
                  <Button 
                    variant="outline"
                    className="inline-flex"
                    title={formatMessage(messages.callToActionSecondary)}
                  >
                    <FormattedMessage {...messages.callToActionSecondary}/>
                  </Button>
                </a>

                <a target="_blank" href={formatMessage(messages.callToActionHref)}>
                  <Button 
                    title={formatMessage(messages.callToAction)}
                  >
                    <FormattedMessage {...messages.callToAction}/>
                  </Button>
                </a>
              </React.Fragment>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:max-w-6xl lg:mx-auto lg:px-8 mt-10 lg:mt-20">
        <RecentOrders />
        {!!importantNotifications.length &&
        <div className="space-y-5 sm:space-y-4 lg:col-span-2">
          <h1 className="text-xl font-bold">
            <FormattedMessage {...messages.importantNotificationTitle} />
          </h1>
          <ul className="w-full">
            {importantNotifications.map(({ id, title, markSeen, url, rating }) => (
              <li
                key={id}
                className="flex items-center justify-between p-4 border-b border-gray-200 transition-transform hover:scale-105"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{title}</p>
                </div>
                <div className="flex items-center space-x-4">
                  {url && (
                    <div className="flex space-x-1 items-center">
                      {[...Array(5)].map((_, i) => {
                        const starValue = i + 1;
                        const isHighlighted =
                          (activeId === id && hoveredRating >= starValue)

                        return (
                          <button
                            key={i}
                            className={`text-2xl transition-colors ${
                              isHighlighted ? "text-yellow-500" : "text-gray-400"
                            } hover:text-yellow-500`}
                            onMouseEnter={() => {
                              setActiveId(id);
                              setHoveredRating(starValue);
                            }}
                            onMouseLeave={() => {
                              setHoveredRating(null);
                              setActiveId(null);
                            }}
                            onClick={() => {
                              onMarkSeen(id, markSeen);
                              window.open(`${url}?rating=${starValue}`);
                            }}
                          >
                            &#9733;
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <Button size="sm" onClick={() => onMarkSeen(id, markSeen)}>
                    <FormattedMessage {...messages.importantNotificationDismiss} />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        }
        <div className="space-y-12 lg:grid lg:grid-cols-4 lg:gap-20 lg:space-y-0">
          <div className="space-y-5 sm:space-y-4 lg:col-span-2">
            <h1 className="text-xl font-bold">
              <FormattedMessage {...messages.latestNewsTitle} />
            </h1>
            <p className="mb-8 mt-4 text text-gray-500">
              <FormattedMessage {...messages.latestNews}/>
            </p>

            <ul className="divide-y divide-gray-200">
              { announcements.map(({ uid, title, body = '', reference, acked }) => (
                <li
                  key={uid}
                  className="py-3 group cursor-pointer"
                  title={acked ? undefined : 'Click to acknowledge ✔'}
                  onClick={(event) => {
                    if (!acked) {
                      ack({ method: 'PUT', body: uid })
                    }

                    onShowModal(body)
                  }}
                >
                  <div className="flex justify-between">
                    <h3 className="grow text-sm font-semibold text-gray-800 group-hover:text-primary-darker truncate">
                      { title }
                    </h3>

                    <div>
                      { !acked ? (
                        <span className="flex relative h-4 w-4 justify-center items-center">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                          <Icon icon="check" className="text-primary-darker" />
                        </span>
                      ) : (
                        <Icon icon="eye" className="text-gray-400" />
                      ) }
                    </div>
                  </div>

                  <p className="text-sm mt-1 text-gray-500">
                    { body.length > 133 ? `${body.slice(0, 130)}...` : body }
                  </p>
                </li>
              )) }
              <li
                key="show-all-announcements"
                className="py-3 group"
              >
                <Link to="/announcements">
                  <div className="space-x-2 text-sm mt-1 text-gray-500">
                    <FormattedMessage
                      {...messages.redirectToAnnouncements}
                      values={{
                        button: (chunks) => (
                          <Button variant="inline">
                            {chunks}
                          </Button>
                        )
                      }}
                    />
                    <Icon
                      icon={['fa', 'envelope-open-text']}
                      className="text-primary"
                    />
                  </div>
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h1 className="text-xl font-bold">
              <FormattedMessage{...messages.meetBranch}/>
            </h1>

            <p className="mb-8 mt-4 text text-gray-500">
              <FormattedMessage{...messages.branchMembers} values={{i: (chunks) => <i className="italic"> { chunks } </i> }}/>
            </p>

            <ul className="space-y-12 sm:grid sm:grid-cols-3 sm:gap-x-6 sm:gap-y-12 sm:space-y-0 lg:gap-x-8">

              { (coordinators || []).map(({ name, title, email, image, telephone, notes, www }) => (
                <li>
                  <div className="space-y-4">
                    <div className="aspect-w-3 aspect-h-2">
                      { image && image !== '' ? (
                        <img className="object-cover shadow rounded h-40 w-40" src={image} alt={name} />
                      ) : (
                        <div className="h-40 w-40 rounded-sm flex justify-center items-center bg-gray-100 shadow">
                          <Icon icon="user" className="text-5xl text-white" />
                        </div>
                      ) }
                    </div>
                    <div className="leading-6 font-medium space-y-1">
                      <h3>{ name }</h3>
                      <p className="text-primary">{ title }</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{ notes }</p>
                    </div>

                    <ul className="flex space-x-5">
                      <li>
                        <a href={`mailto:${email}`} className="text-gray-400 hover:text-gray-500" target="_blank">
                          <span className="sr-only"><FormattedMessage{...messages.sendEmail}/></span>
                          <Icon icon="at" />
                        </a>
                      </li>
                      <li>
                        <a href={`tel:${telephone}`} className="text-gray-400 hover:text-gray-500" target="_blank">
                          <span className="sr-only"><FormattedMessage{...messages.call}/></span>
                          <Icon icon="phone" />
                        </a>
                      </li>
                      <li>
                        <a href={www} className="text-gray-400 hover:text-gray-500" target="_blank">
                          <span className="sr-only"><FormattedMessage{...messages.connect}/></span>
                          <Icon icon="link" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </li>
              )) }
            </ul>
          </div>
        </div>

        <div className="my-6 border-t border-gray-200 pt-6 space-y-6">
          <h1 className="text-xl font-bold">
            <FormattedMessage {...messages.quickLinks} />
          </h1>

          <ul role="list" className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            { ressources.map((ressource, ressourceIdx) => (
              <li key={ressourceIdx} className="flow-root">
                <div className="relative -m-2 p-2 flex items-center space-x-4 rounded-sm hover:bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500">
                  <div className={`${ressource.bgColor} shrink-0 flex items-center justify-center h-16 w-16 rounded items-center`}>
                    <Icon icon={ressource.icon} className="text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      { ressource.href ? (
                        <a href={ressource.href} className="focus:outline-none">
                          <span className="absolute inset-0" aria-hidden="true" />
                          {ressource.name}
                          <span aria-hidden="true"> &rarr;</span>
                        </a>
                      ) : (
                        <Link to={ressource.path}>
                          <span className="absolute inset-0" aria-hidden="true" />
                          {ressource.name}
                          <span aria-hidden="true"> &rarr;</span>
                        </Link>
                      ) }
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">{ressource.description}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

      </div>
    </main>
  )
}

export default CustomerHomePage
